import { useFetcher } from '@remix-run/react';
import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getFetcherResult } from './remix-utils';

export function usePrevious<T>(value: T): MutableRefObject<T | undefined>['current'] {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// naive implement: replace me when we use react-use
export function useLocalStorage<T>({
  key,
  defaultValue,
  defaultValueOnSSR,
}: {
  key: string;
  defaultValue?: T;
  defaultValueOnSSR?: T;
}) {
  const [state, setState] = useState<T | undefined>(() => {
    if (globalThis.window === undefined) {
      return defaultValueOnSSR ?? defaultValue;
    }
    try {
      const storedValue = globalThis.localStorage.getItem(key);
      return storedValue ? (JSON.parse(storedValue) as T) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setLocalStorageState = useCallback(
    (value: T | ((val: T | undefined) => T)) => {
      setState((prevState) => {
        // @ts-expect-error TS2345: Argument of type 'T | ((val: T | undefined) => T)' is not assignable to parameter of type 'T | ((val: T | undefined) => T)'.
        const valueToStore = typeof value === 'function' ? value(prevState) : value;
        try {
          if (globalThis.window !== undefined) {
            globalThis.localStorage.setItem(key, JSON.stringify(valueToStore));
          }
        } catch (error) {
          console.error(`Error setting localStorage key "${key}":`, error);
        }
        return valueToStore;
      });
    },
    [key],
  );

  return [state, setLocalStorageState] as const;
}

interface ActionOptions<T extends (...args: any[]) => any> {
  key?: string;
  action?: string;
  callback?: T;
  handleOnError?: boolean;
  path: string;
}

// universal error?
function useInsoAction<ActionType, T extends (...args: any[]) => any>({
  key,
  action,
  callback,
  handleOnError = false,
  path,
}: ActionOptions<T>) {
  const fetcher = useFetcher<ActionType>({ key });
  const loading = fetcher.state !== 'idle';
  const { submit: oSubmit, data: oData, state: oState } = fetcher;
  const [error, setError] = useState<string | Record<string, string>>('');
  const submit = useCallback(
    (payload: Record<string, unknown>) => {
      return oSubmit(
        {
          action: action ?? '',
          ...payload,
        },
        { method: 'POST', action: path },
      );
    },
    [oSubmit, action, path], // action and path should not change
  );

  const callbackRef = useRef(callback);
  useLayoutEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const result = getFetcherResult({ data: oData, state: oState });
    const { ok, error } = result;
    if (error) {
      setError(error);
      if (handleOnError) {
        const cleanup = callbackRef.current?.(result);
        if (cleanup && typeof cleanup === 'function') {
          return cleanup;
        }
      }
    } else if (ok) {
      setError('');
      const cleanup = callbackRef.current?.(result);
      if (cleanup && typeof cleanup === 'function') {
        return cleanup;
      }
    }
  }, [oData, oState, handleOnError]); // handleOnError should not change

  const fieldErrors = typeof error === 'object' && error ? error : {};
  const setFieldError = (field: string, message: string) => {
    setError((prev) => ({ ...(typeof prev === 'object' && prev ? prev : {}), [field]: message }));
  };

  return {
    fetcher,
    loading,
    state: fetcher.state,
    data: fetcher.data,
    submit,
    error: typeof error === 'object' ? '' : error,
    setError,
    fieldErrors,
    setFieldError,
  };
}

export function createUseInsoAction<ActionType>(path: string) {
  return <T extends (...args: any[]) => any>(options: Omit<ActionOptions<T>, 'path'>) =>
    useInsoAction<ActionType, T>({ ...options, path });
}

// Copy from https://github.com/sanity-io/use-effect-event/blob/main/src/useEffectEvent.ts
function forbiddenInRender() {
  throw new Error("A function wrapped in useEffectEvent can't be called during rendering.");
}
const context = React.createContext(true);
const isInvalidExecutionContextForEventFunction =
  'use' in React
    ? () => {
        // There's no way to check if we're in a render phase from outside of React, the API used by useEffectEvent is private: https://github.com/facebook/react/blob/a00ca6f6b51e46a0ccec54a2231bfe7a1ed9ae1d/packages/react-reconciler/src/ReactFiberWorkLoop.js#L1785-L1788
        // So to emulate the same behavior, we call the use hook and if it doesn't throw, we're in a render phase.
        try {
          /* eslint-disable */
          // @ts-ignore
          return React.use(context);
          /* eslint-enable */
        } catch {
          return false;
        }
      }
    : () => false;

export function useEffectEvent<const T extends (...args: any[]) => void>(fn: T): T {
  const ref = React.useRef(forbiddenInRender as T);

  React.useInsertionEffect(() => {
    ref.current = fn;
  }, [fn]);

  return ((...args: any) => {
    if (isInvalidExecutionContextForEventFunction()) {
      forbiddenInRender();
    }

    const latestFn = ref.current!;
    return latestFn(...args);
  }) as T;
}
